<template>
  <component
    :is="component"
    class="ui-outline-button"
    :class="[
      `ui-outline-button--${theme}`,
      {
        'is-active': active,
        'is-disabled': disabled,
        'is-full-width': fullWidth,
      },
    ]"
    v-bind="componentAttributes"
    v-on="$listeners"
    @dragstart.prevent
  >
    <span class="ui-outline-button__label"><slot /></span>
  </component>
</template>

<script>
export default {
  name: 'UiOutlineButton',
  inheritAttrs: false,

  props: {
    theme: {
      type: String,
      default: 'dark',
      validator: (value) => ['dark', 'white'].includes(value)
    },
    tag: {
      type: String,
      default: ''
    },
    to: {
      type: [String, Object],
      default: ''
    },
    href: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: 'button'
    },
    active: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    fullWidth: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    component () {
      if (this.to) return 'router-link'
      if (this.href) return 'a'
      return this.tag || 'button'
    },

    componentAttributes () {
      const attributes = { ...this.$attrs }

      if (this.to) attributes.to = this.to
      if (this.href) attributes.href = this.href
      if (this.to || this.href) attributes.draggable = 'false'
      if (!this.to && !this.href && !this.tag) attributes.type = this.type

      if (this.active) { attributes['aria-current'] = attributes['aria-current'] || 'page' }

      if (this.disabled) {
        attributes['aria-disabled'] = 'true'
        attributes.tabindex = -1
        if (!this.to && !this.href && !this.tag) attributes.disabled = true
      }

      return attributes
    }
  }
}
</script>

<style lang="scss" scoped>
@import "./tokens";

.ui-outline-button {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  height: vw(36);
  padding: 0 vw(20);
  border: vw(1) solid;
  border-radius: vw(20);
  background: transparent;
  font-family: "Manrope", Arial, sans-serif;
  font-size: vw(14);
  font-style: normal;
  font-weight: 400;
  line-height: vw(19);
  text-align: center;
  text-transform: uppercase;
  white-space: nowrap;
  user-select: none;
  -webkit-user-drag: none;
  cursor: pointer;
  appearance: none;
  transition: background-color 0.3s ease, color 0.3s ease;

  & > &__label {
    display: block;
    flex: 0 0 auto;
    font-family: "Manrope", Arial, sans-serif;
    line-height: inherit;
    text-align: center;
    white-space: nowrap;
  }

  &--dark {
    border-color: $ui-color-text;
    color: $ui-color-text;

    &:hover,
    &:active,
    &.is-active,
    &.router-link-active {
      background: $ui-color-hover;
    }
  }

  &--white {
    border-color: $ui-color-white;
    color: $ui-color-white;

    &:hover,
    &:active,
    &.is-active,
    &.router-link-active {
      background: $ui-color-white;
      color: $ui-color-text;
    }
  }

  &.is-disabled,
  &:disabled {
    pointer-events: none;
    cursor: default;
    opacity: 0.45;
  }

  &.is-full-width {
    width: 100%;
  }
}

@include max-width(mobile) {
  .ui-outline-button {
    height: vwm(35);
    padding: 0 vwm(20);
    border-width: vwm(1);
    border-radius: vwm(20);
    font-size: vwm(12);
    line-height: vwm(16);
  }
}
</style>
